import { FormInstance } from 'antd';
import { useCallback } from 'react';
import { RateCardLite, RateCardVersion } from 'api/rate-cards';
import { toastService } from 'components/molecules/toast';
import { useTranslation } from 'hooks/use-translation';
import { useUpdateRateCardVersionMutation } from 'queries/rate-cards';
import { RateCardCreationMeta } from '../../types';

interface RateCardVersionUpdateHanderProps {
  selectedRateCard: RateCardLite | undefined;
  form: FormInstance<RateCardCreationMeta>;
  getRateCardVersionPayload: () => RateCardVersion;
  onRateCardVersionUpdate: (updatedRateCardVersion: RateCardVersion) => void;
}

interface ReturnProps {
  updateRateCardVersionHandler: () => Promise<RateCardVersion | undefined>;
  isRateCardVersionUpdationInProgress: boolean;
}

export const useRateCardVersionUpdate = ({
  selectedRateCard,
  form,
  getRateCardVersionPayload,
  onRateCardVersionUpdate,
}: RateCardVersionUpdateHanderProps): ReturnProps => {
  const succesTitle = useTranslation(
    'settings.rate_cards.rate_card_version.update.toast.success.title',
  );
  const successMessage = useTranslation(
    'settings.rate_cards.rate_card_version.update.toast.success.message',
  );
  const errorTitle = useTranslation(
    'settings.rate_cards.rate_card_version.update.toast.error.title',
  );
  const errorMessage = useTranslation(
    'settings.rate_cards.rate_card_version.update.toast.error.message',
  );

  const {
    mutateAsync: updateRateCardVersionMutation,
    isLoading: isRateCardVersionUpdationInProgress,
  } = useUpdateRateCardVersionMutation();

  const updateRateCardVersionHandler = useCallback(async () => {
    if (selectedRateCard == null) {
      return undefined;
    }

    const rateCardVersionPayload = getRateCardVersionPayload();

    try {
      const updatedVersion = await updateRateCardVersionMutation(
        rateCardVersionPayload,
      );

      toastService({
        title: succesTitle,
        message: successMessage,
      }).success();
      form.resetFields();
      onRateCardVersionUpdate(updatedVersion);

      return updatedVersion;
    } catch (e) {
      toastService({
        title: errorTitle,
        message: errorMessage,
      }).error();

      return undefined;
    }
  }, [
    selectedRateCard,
    getRateCardVersionPayload,
    updateRateCardVersionMutation,
    succesTitle,
    successMessage,
    form,
    onRateCardVersionUpdate,
    errorTitle,
    errorMessage,
  ]);

  return {
    updateRateCardVersionHandler,
    isRateCardVersionUpdationInProgress,
  };
};
